import React from 'react';
import { Box, Stack } from '@mui/material';
import { DictionaryIrregularVerb } from 'app/types';
import UICard from 'components/ui/UICard';
import UIFlex from 'components/ui/UIFlex';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import ProgressBar from 'components/ProgressBar';
import { CEFR_META, hexToRgba } from 'components/cefrMeta';
import { LEVELS, dieslova } from './patternSuperGroups';
import { aggregateByLevel } from './aggregations';
import { Selection } from './urlState';
import DrillHeader from './DrillHeader';
import IrregularVerbsTable from './IrregularVerbsTable';

// Matches the /wordset row accent for "irregular-verb" — same brand the
// catalog row uses when sending the user here, so the card frame on the
// inside page reads as a continuation of that row's colour scheme.
const LEVEL_ACCENT = '#3B2C93';

const StatPill: React.FC<{ n: number; u: string }> = ({ n, u }) => (
  <UIBox
    sx={{
      alignItems: 'baseline',
      bgcolor: 'background.default',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 999,
      display: 'inline-flex',
      fontSize: 13,
      gap: 0.6,
      px: 1.4,
      py: 0.6,
      whiteSpace: 'nowrap',
    }}
  >
    <UIText component={'b'} sx={{ color: 'text.primary', fontWeight: 800 }}>
      {n}
    </UIText>
    <UIText component={'span'} color={'primary.subtitle'} sx={{ fontSize: 13 }}>
      {u}
    </UIText>
  </UIBox>
);

interface Props {
  verbs: readonly DictionaryIrregularVerb[];
  selection: Selection | null;
  onSelectLevel: (level: string) => void;
  onClearSelection: () => void;
}

const LevelsMode: React.FC<Props> = ({
  verbs,
  selection,
  onSelectLevel,
  onClearSelection,
}) => {
  if (selection?.kind === 'level') {
    const filtered = verbs.filter((v) => v.levelBased === selection.value);
    const meta = LEVELS.find((l) => l.key === selection.value);
    return (
      <Box>
        <DrillHeader
          backLabel="До списку рівнів"
          title={`Рівень ${selection.value} · ${meta?.name ?? ''}`}
          count={filtered.length}
          onBack={onClearSelection}
        />
        <IrregularVerbsTable verbs={filtered} />
      </Box>
    );
  }

  const agg = aggregateByLevel(verbs);

  return (
    <Box>
      <Box sx={{ mb: 2, fontSize: 14, color: 'text.secondary' }}>
        Неправильні дієслова за рівнем складності CEFR. Натисни рівень, щоб
        побачити список.
      </Box>
      <Stack spacing={1.5}>
        {LEVELS.map((l) => {
          const a = agg.get(l.key) ?? { total: 0, learned: 0 };
          const cefr = CEFR_META[l.key];
          const started = a.learned > 0;

          return (
            <UICard
              key={l.key}
              onClick={() => onSelectLevel(l.key)}
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                p: 0,
                transition: 'border-color .16s ease',
                '&:hover': {
                  borderColor: LEVEL_ACCENT,
                  bgcolor: hexToRgba(LEVEL_ACCENT, 0.04),
                },
                '&:hover .vlc-arrow': { transform: 'translateX(4px)' },
              }}
            >
              <UIFlex
                ai={'center'}
                sx={{
                  flexWrap: 'nowrap',
                  gap: { xs: 1.75, md: 2.5 },
                  p: { xs: '14px', md: '16px 20px' },
                }}
              >
                {/* CEFR badge thumbnail — same chrome as /wordset/level-based */}
                <UIBox
                  sx={{
                    alignItems: 'center',
                    bgcolor: hexToRgba(LEVEL_ACCENT, 0.1),
                    borderRadius: 2,
                    display: 'flex',
                    flex: 'none',
                    height: { xs: 60, md: 96 },
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                    width: { xs: 60, md: 96 },
                  }}
                >
                  {cefr && (
                    <UIBox
                      sx={{
                        filter: 'drop-shadow(0 2px 6px rgba(20,20,20,0.16))',
                        '& svg': {
                          width: { xs: 36, md: 62 },
                          height: 'auto',
                        },
                      }}
                    >
                      <cefr.Icon />
                    </UIBox>
                  )}
                </UIBox>

                {/* Content */}
                <UIBox sx={{ flex: 1, minWidth: 0 }}>
                  <UIText
                    sx={{
                      // English/code eyebrow ("A1 · Beginner") drops to
                      // default text — same en-black / ua-accent rule
                      // CategoryCard uses on /wordset/level-based.
                      color: 'text.primary',
                      fontSize: { xs: 10, md: 11 },
                      fontWeight: 800,
                      letterSpacing: '0.09em',
                      mb: 0.5,
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {`${l.key} · ${cefr?.en ?? l.name}`}
                  </UIText>
                  <UIText
                    variant={'h3'}
                    sx={{
                      color: LEVEL_ACCENT,
                      fontSize: { xs: 17, md: 21 },
                      mb: 0.6,
                    }}
                  >
                    {`Неправильні дієслова — ${l.name}`}
                  </UIText>
                  <UIText
                    color={'primary.subtitle'}
                    sx={{
                      display: '-webkit-box',
                      fontSize: { xs: 13, md: 14 },
                      mb: 1.25,
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: { xs: 2, md: 1 },
                    }}
                  >
                    {`Підбірка неправильних дієслів рівня ${l.key} (${l.name.toLowerCase()}) — три форми, переклад і вправи.`}
                  </UIText>
                  <UIFlex jc={'flex-start'} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <StatPill n={a.total} u={dieslova(a.total)} />
                    {a.learned > 0 && (
                      <StatPill n={a.learned} u={'вивчено'} />
                    )}
                  </UIFlex>
                </UIBox>

                {/* Right cluster: progress + CTA — desktop only */}
                <UIBox
                  sx={{
                    alignItems: 'stretch',
                    display: { xs: 'none', md: 'flex' },
                    flex: 'none',
                    flexDirection: 'column',
                    gap: 1.5,
                    width: 250,
                  }}
                >
                  {started && (
                    <ProgressBar
                      addedValue={a.total}
                      learnedValue={a.learned}
                      totalValue={a.total}
                      minWidth={'100%'}
                      maxWidth={'100%'}
                      width={'100%'}
                    />
                  )}
                  <UIBox
                    sx={{
                      alignItems: 'center',
                      color: LEVEL_ACCENT,
                      display: 'inline-flex',
                      fontSize: 15,
                      fontWeight: 700,
                      gap: 0.5,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {'Відкрити'}
                    <UIBox
                      className={'vlc-arrow'}
                      component={'span'}
                      sx={{
                        display: 'inline-block',
                        transition:
                          'transform .25s cubic-bezier(0.22,1,0.36,1)',
                      }}
                    >
                      {'→'}
                    </UIBox>
                  </UIBox>
                </UIBox>
              </UIFlex>
            </UICard>
          );
        })}
      </Stack>
    </Box>
  );
};

export default LevelsMode;
